import KmxCheckStroked from '@kmx/mui-icons/KmxCheckStroked';
import KmxClockStroked from '@kmx/mui-icons/KmxClockStroked';
import KmxFireFilled from '@kmx/mui-icons/KmxFireFilled';
import KmxLightningBoltFilled from '@kmx/mui-icons/KmxLightningBoltFilled';
import Chip, { type ChipProps } from '@mui/material/Chip';
import { styled } from '@mui/material/styles';
import { memo } from 'react';

import type { CarStatus } from '../../types/CarStatus';

const BADGE_CLASSNAME = 'kmx-car-tile__status-badge';
const BADGE_TEST_ID = 'badge';

// NOTE: Order is important here, as we can only display one badge!
const STATUSES: Array<{ status: keyof CarStatus; label: string }> = [
  { status: 'isSold', label: 'Sold' },
  { status: 'isReservedByYou', label: 'Reserved' },
  { status: 'isShipping', label: 'On hold' },
  { status: 'isNotAvailable', label: 'On hold' },
  { status: 'isNotSaleable', label: 'On hold' },
  { status: 'isOnTestDrive', label: 'On hold' },
  { status: 'isReserved', label: 'On hold' },
  { status: 'isRecentlyViewed', label: 'Recently viewed' },
  { status: 'isComingSoon', label: 'Coming soon' },
  { status: 'isPopular', label: 'Popular' },
  { status: 'isReadyToday', label: 'Ready today' },
  { status: 'isFastShipping', label: 'Fast shipping' },
  { status: 'isNewMatch', label: 'New for you' },
  { status: 'hasPriceDrop', label: 'Price drop' },
];

const DefaultChip = styled(Chip)<ChipProps>(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
}));

const PopularIcon = styled(KmxFireFilled)(({ theme }) => ({
  color: `${theme.palette.warning.light} !important`,
}));

const OnHoldIcon = styled(KmxClockStroked)(({ theme }) => ({
  color: `${theme.palette.primary.main} !important`,
}));

const FastShippingIcon = styled(KmxLightningBoltFilled)(({ theme }) => ({
  color: `${theme.palette.primary.main} !important`,
}));

const ReservedIcon = styled(KmxCheckStroked)(({ theme }) => ({
  color: `${theme.palette.primary.main} !important`,
}));

type BadgeProps = { carStatus: CarStatus; size?: ChipProps['size'] };

function StatusBadge({ carStatus, size }: BadgeProps) {
  const badgeStatus = STATUSES.find(({ status }) =>
    Boolean(carStatus?.[status]),
  );

  if (!badgeStatus) {
    return null;
  }

  if (badgeStatus.status === 'isSold') {
    return (
      <Chip
        className={BADGE_CLASSNAME}
        color="secondary"
        data-sold="true"
        data-testid={BADGE_TEST_ID}
        label={badgeStatus.label}
        size={size}
        variant="filled"
      />
    );
  }

  if (['isReservedByYou'].includes(badgeStatus.status)) {
    return (
      <Chip
        className={BADGE_CLASSNAME}
        color="primary"
        data-reserved="true"
        data-testid={BADGE_TEST_ID}
        icon={<ReservedIcon />}
        label={badgeStatus.label}
        size={size}
        variant="weak"
      />
    );
  }

  if (badgeStatus.status === 'isPopular') {
    return (
      <DefaultChip
        className={BADGE_CLASSNAME}
        data-popular="true"
        data-testid={BADGE_TEST_ID}
        icon={<PopularIcon />}
        label={badgeStatus.label}
        size={size}
        variant="outlined"
      />
    );
  }

  if (badgeStatus.status === 'hasPriceDrop') {
    return (
      <Chip
        className={BADGE_CLASSNAME}
        color="success"
        data-price-drop="true"
        data-testid={BADGE_TEST_ID}
        label={badgeStatus.label}
        size={size}
      />
    );
  }

  if (
    [
      'isNotAvailable',
      'isNotSaleable',
      'isOnTestDrive',
      'isShipping',
      'isReserved',
    ].includes(badgeStatus.status)
  ) {
    return (
      <DefaultChip
        className={BADGE_CLASSNAME}
        data-on-hold="true"
        data-testid={BADGE_TEST_ID}
        icon={<OnHoldIcon />}
        label={badgeStatus.label}
        size={size}
        variant="outlined"
      />
    );
  }

  if (badgeStatus.status === 'isFastShipping') {
    return (
      <DefaultChip
        className={BADGE_CLASSNAME}
        data-fast-shipping="true"
        data-testid={BADGE_TEST_ID}
        icon={<FastShippingIcon />}
        label={badgeStatus.label}
        size={size}
        variant="outlined"
      />
    );
  }

  return (
    <DefaultChip
      className={BADGE_CLASSNAME}
      data-testid={BADGE_TEST_ID}
      label={badgeStatus.label}
      size={size}
      variant="outlined"
    />
  );
}

export default memo(StatusBadge);
